import { useLayoutEffect } from 'react';

/**
 * @description intentionally in a separate .js file as its types are needlessly
 * complex due to Highcharts series ambiguity & mutating it on top
 * @see Chart.tsx
 */
export default function useTimeLapse(initialOptions, setOptions) {
  const timeLapse = initialOptions.timeLapse;

  useLayoutEffect(() => {
    if (!timeLapse) return;

    const nonEmptySeries = initialOptions.series.filter(
      (series) => series.data.length > 0
    );

    let timestamp = nonEmptySeries
      .map((series) => series.data[0][timeLapse.dataPointKey])
      .reduce((a, b) => Math.min(a, b));
    const maxTimestamp = nonEmptySeries
      .map(
        (series) => series.data[series.data.length - 1][timeLapse.dataPointKey]
      )
      .reduce((a, b) => Math.max(a, b));

    const allData = nonEmptySeries.flatMap((series) => series.data);
    const minX = allData.reduce(
      (a, b) => Math.min(a, b.x),
      Number.MAX_SAFE_INTEGER
    );
    const maxX = allData.reduce(
      (a, b) => Math.max(a, b.x),
      Number.MIN_SAFE_INTEGER
    );
    const minY = allData.reduce(
      (a, b) => Math.min(a, b.y),
      Number.MAX_SAFE_INTEGER
    );
    const maxY = allData.reduce(
      (a, b) => Math.max(a, b.y),
      Number.MIN_SAFE_INTEGER
    );

    const width = maxX - minX;
    const height = maxY - minY;
    const largestDimension = Math.max(width, height);
    const x = minX + width / 2;
    const y = minY + height / 2;
    const x1 = x - largestDimension / 2;
    const x2 = x + largestDimension / 2;
    const y1 = y - largestDimension / 2;
    const y2 = y + largestDimension / 2;

    setOptions((prev) => ({
      ...prev,
      xAxis: {
        ...(prev.xAxis ?? {}),
        min: x1,
        max: x2,
      },
      yAxis: {
        ...(prev.yAxis ?? {}),
        min: y1,
        max: y2,
      },
      series: prev.series.map((series) => ({
        ...series,
        data: [],
      })),
    }));

    let timeout = setTimeout(function tick(isFirstTick = true) {
      const tickStartTime = performance.now();

      setOptions((prev) => {
        prev.series.forEach((series, index) => {
          if (series.isTrailSeries) {
            series.data = prev.series[series.relatedSeriesIndex].data.filter(
              (data) =>
                data[timeLapse.dataPointKey] >
                timestamp - timeLapse.trailSeries.dataInterval
            );
            return;
          }

          if (series.isCurrentDataPointSeries) {
            series.data = prev.series[series.relatedSeriesIndex].data.slice(-1);
            return;
          }

          const fullData = initialOptions.series[index].data;
          series.data = fullData.filter(
            (data) => data[timeLapse.dataPointKey] <= timestamp
          );
        });

        if (
          isFirstTick &&
          (timeLapse.trailSeries || timeLapse.currentDataPointSeries)
        ) {
          prev.series = prev.series.concat(
            ...[
              ...(timeLapse.trailSeries
                ? prev.series.map((series, index) => ({
                    ...series,
                    ...timeLapse.trailSeries,
                    isTrailSeries: true,
                    relatedSeriesIndex: index,
                    data: [],
                  }))
                : []),
              ...(timeLapse.currentDataPointSeries
                ? prev.series.map((series, index) => ({
                    ...timeLapse.currentDataPointSeries,
                    isCurrentDataPointSeries: true,
                    relatedSeriesIndex: index,
                    data: series.data.slice(-1),
                    marker: {
                      ...(timeLapse.currentDataPointSeries.marker ?? {}),
                      fillColor: series.color ?? 'white',
                    },
                  }))
                : []),
            ]
          );
        }

        return {
          ...prev,
        };
      });

      const tickDuration = performance.now() - tickStartTime;

      timestamp += timeLapse.dataInterval;
      if (timestamp < maxTimestamp) {
        timeout = setTimeout(
          () => tick(false),
          Math.max(1, timeLapse.visualInterval - tickDuration)
        );
      } else {
        setOptions((prev) => ({
          ...prev,
          series: prev.series.filter(
            (series) =>
              !series.isTrailSeries && !series.isCurrentDataPointSeries
          ),
        }));
      }
    }, timeLapse.initialDelay ?? 0);

    return () => clearTimeout(timeout);
  }, [initialOptions.series, setOptions, timeLapse]);
}
